import {
  getAuth,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  GoogleAuthProvider,
  browserPopupRedirectResolver,
  User,
} from 'firebase/auth'
import { doc, setDoc } from 'firebase/firestore'
import { getAuthUserProfile } from './getAuthUserProfile'
import { shouldUseGoogleRedirect } from './shouldUseGoogleRedirect'
import { PrivateUserInfoType } from '@otw/models'
import { FirebaseError } from 'firebase/app'
import { MultiFactorError } from '@firebase/auth'
import { db } from '@otw/models/db'
import type { Dispatch, SetStateAction } from 'react'

type SetUserInfo = Dispatch<SetStateAction<PrivateUserInfoType>>

export const applyGoogleSignedInUser = async (
  user: User,
  setUserInfo: SetUserInfo,
): Promise<PrivateUserInfoType> => {
  let userProfile = await getAuthUserProfile(user)

  if (!userProfile && user.email) {
    const newUserData: PrivateUserInfoType = {
      email: user.email,
      first_name: user.displayName?.split(' ')[0] || '',
      last_name: user.displayName?.split(' ').slice(1).join(' ') || '',
      role: 'non-admin',
      disabled: false,
      email_verified: user.emailVerified,
      permissions: [],
      id: user.uid,
      dealership_name: '',
      is_active: false,
      displayName: user.displayName || '',
    }

    await setDoc(doc(db.users, user.uid), newUserData)
    userProfile = await getAuthUserProfile(user)
  }

  if (!userProfile) {
    throw new Error('Failed to get or create user profile')
  }

  setUserInfo(userProfile)
  return userProfile
}

const rethrowGoogleAuthError = (error: unknown): never => {
  const err = error as { code?: string; message?: string; name?: string }
  console.error('Google login error details:', {
    error,
    code: err?.code,
    message: err?.message,
    name: err?.name,
  })
  throw error
}

export const completeGoogleRedirectIfPresent = async (
  setUserInfo: SetUserInfo,
): Promise<void> => {
  const auth = getAuth()
  try {
    const result = await getRedirectResult(auth)
    if (!result?.user) return
    await applyGoogleSignedInUser(result.user, setUserInfo)
  } catch (error) {
    rethrowGoogleAuthError(error)
  }
}

export const handleLoginWithGoogle = async (
  setUserInfo: SetUserInfo,
): Promise<MultiFactorError | FirebaseError | void> => {
  const auth = getAuth()
  const provider = new GoogleAuthProvider()

  try {
    if (shouldUseGoogleRedirect()) {
      await signInWithRedirect(auth, provider)
      return
    }

    const { user } = await signInWithPopup(auth, provider, browserPopupRedirectResolver)
    await applyGoogleSignedInUser(user, setUserInfo)
  } catch (error: unknown) {
    rethrowGoogleAuthError(error)
  }
}
